import React from 'react';
import { WidgetBadge } from './WidgetBadge';
import type { BadgeType } from '../config/permissions';

interface WidgetHeaderProps {
  title: string;
  children?: React.ReactNode;
  /** Optional indicator element (e.g., live status dot) shown after title */
  indicator?: React.ReactNode;
  /** Optional badge to display (admin, beta, new, updated) */
  badge?: BadgeType | null;
}

/**
 * Shared widget header with drag handle affordance.
 *
 * Features:
 * - Grip icon (6 dots) visible on hover for drag affordance
 * - cursor-grab on hover, cursor-grabbing while dragging (via CSS)
 * - Optional badge display (Admin, Beta, New, Updated)
 * - Consistent styling across all widgets
 *
 * Usage:
 * <WidgetHeader title="My Widget" badge="beta" indicator={<LiveDot />}>
 *   <button>Settings</button>
 * </WidgetHeader>
 */
export const WidgetHeader: React.FC<WidgetHeaderProps> = ({ title, children, indicator, badge }) => {
  return (
    <div className="widget-drag-handle group flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-grab active:cursor-grabbing relative z-20">
      {/* Grip handle comes from the shared .widget-drag-handle::before rule in
          index.css, same as every other widget header. This component used to
          render its own SVG grip on top of that, which double-indented the
          title relative to the other 23 headers. */}

      {/* Title */}
      <h3 className="text-sm font-bold text-white truncate uppercase tracking-[1.5px]">{title}</h3>

      {/* Optional badge */}
      {badge && (
        <div className="flex-shrink-0 ml-2">
          <WidgetBadge type={badge} size="sm" />
        </div>
      )}

      {/* Spacer */}
      <div className="flex-1 min-w-0" />

      {/* Optional indicator (e.g., live status) */}
      {indicator && (
        <div className="flex-shrink-0 ml-2">
          {indicator}
        </div>
      )}

      {/* Optional right-side content (settings buttons, etc.) */}
      {children && (
        <div className="flex-shrink-0 ml-2 flex items-center gap-1">
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * Live status indicator dot for real-time widgets.
 */
export const LiveIndicator: React.FC<{ isConnected: boolean }> = ({ isConnected }) => {
  if (!isConnected) return null;

  return (
    <span
      className="w-2 h-2 rounded-full bg-bull-blue animate-pulse"
      title="Live"
    />
  );
};
